import type { CalendarDay } from "@/features/towers/utils/tripActiveOnCalendarDay";

import {
  hasTripViewListQueryParams,
  serializeTripViewBrowseLandingDaySearch,
} from "./tripViewListQueryParams";

const SNAPSHOT_STORAGE_KEY = "towers.tripView.snapshot";
const RESTORE_PENDING_STORAGE_KEY = "towers.tripView.restorePending";
const RESTORE_HYDRATED_LOCATION_KEY =
  "towers.tripView.restoreHydratedLocationKey";

let reloadDocumentSessionResetDone = false;
let reloadDocumentEntryPathname: string | null = null;
let reloadTripViewEntryMountConsumed = false;

let sectionRootFreshTripViewEntryPending = false;
let suppressTripViewSnapshotWrites = false;

/** @internal Vitest only */
export function resetTripViewReloadBrowseLandingLatchForTests(): void {
  reloadDocumentSessionResetDone = false;
  reloadDocumentEntryPathname = null;
  reloadTripViewEntryMountConsumed = false;
}

/** @internal Vitest only */
export function resetTripViewSectionRootFreshEntryForTests(): void {
  sectionRootFreshTripViewEntryPending = false;
  suppressTripViewSnapshotWrites = false;
}

export function isTripViewSectionRootFreshEntryPending(): boolean {
  return sectionRootFreshTripViewEntryPending;
}

function markTripViewFreshEntryFromSectionRoot(): void {
  sectionRootFreshTripViewEntryPending = true;
  suppressTripViewSnapshotWrites = true;
}

function consumeTripViewFreshEntryFromSectionRoot(): boolean {
  if (!sectionRootFreshTripViewEntryPending) {
    return false;
  }
  sectionRootFreshTripViewEntryPending = false;
  suppressTripViewSnapshotWrites = false;
  return true;
}

/**
 * Records browser reload at Towers shell entry. Clears session only when the
 * reloaded URL has no trip-list query params (URL owns list state when present).
 */
export function noteTripViewDocumentReloadAtShell(pathname: string): void {
  if (!isBrowserReloadNavigation() || reloadDocumentSessionResetDone) {
    return;
  }
  reloadDocumentSessionResetDone = true;
  reloadDocumentEntryPathname = pathname;

  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (hasTripViewListQueryParams(params)) {
      return;
    }
  }

  clearTripViewSession();
}

/** Intelligence Towers section root (parent sidebar); always fresh trip browse landing. */
export const TOWERS_SECTION_ROOT_PATH = "/app/towers";

export const TRIP_VIEW_BROWSE_PATH = "/app/towers/trip";

/**
 * Trip View session: optional pathname snapshot (legacy). Towers Trip tab always
 * opens browse landing; list filters and stage tab are URL-only.
 */
export type TripViewSessionSnapshot = {
  pathname: string;
};

export type TripViewLocationState = {
  tripViewInAppSelect?: boolean;
};

export function isTripViewInAppTableSelectState(state: unknown): boolean {
  return (state as TripViewLocationState | null)?.tripViewInAppSelect === true;
}

export function tripViewPathForTripId(tripId: string | null): string {
  return tripId ? `/app/towers/trip/${tripId}` : TRIP_VIEW_BROWSE_PATH;
}

export function isTripViewPathname(pathname: string): boolean {
  return (
    pathname === TRIP_VIEW_BROWSE_PATH ||
    pathname.startsWith(`${TRIP_VIEW_BROWSE_PATH}/`)
  );
}

export function isTowersPathname(pathname: string): boolean {
  return (
    pathname === TOWERS_SECTION_ROOT_PATH ||
    pathname.startsWith(`${TOWERS_SECTION_ROOT_PATH}/`)
  );
}

export function isTowersSectionRootPathname(pathname: string): boolean {
  return pathname === TOWERS_SECTION_ROOT_PATH;
}

export type HandleTripViewTowersNavigationOptions = {
  shellEntry?: { isHistoryTraversal: boolean };
};

/**
 * Updates restore flags when the user moves between Towers routes in the SPA.
 */
export function handleTripViewTowersNavigation(
  previousPathname: string | null,
  nextPathname: string,
  options?: HandleTripViewTowersNavigationOptions,
): void {
  const isOnTripView = isTripViewPathname(nextPathname);
  const isOnTowers = isTowersPathname(nextPathname);
  const wasOnTripView =
    previousPathname != null && isTripViewPathname(previousPathname);

  if (isTowersSectionRootPathname(nextPathname)) {
    clearTripViewSession();
    markTripViewFreshEntryFromSectionRoot();
    return;
  }

  if (previousPathname === null && isOnTripView) {
    if (options?.shellEntry?.isHistoryTraversal) {
      return;
    }
    if (isTripViewRestorePending()) {
      const snapshot = readTripViewSessionSnapshot();
      if (snapshot != null && isTripViewPathname(snapshot.pathname)) {
        return;
      }
    }
    clearTripViewSession();
    return;
  }

  if (wasOnTripView && isOnTowers && !isOnTripView) {
    clearTripViewRestoreHydrated();
    markTripViewRestorePending();
    return;
  }

  if (wasOnTripView && !isOnTowers) {
    clearTripViewRestoreHydrated();
    markTripViewRestorePending();
  }
}

export function isBrowserReloadNavigation(): boolean {
  if (typeof performance === "undefined") {
    return false;
  }
  const entry = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;
  return entry?.type === "reload";
}

export function readTripViewSessionSnapshot(): TripViewSessionSnapshot | null {
  try {
    const raw = sessionStorage.getItem(SNAPSHOT_STORAGE_KEY);
    if (raw == null) {
      return null;
    }
    return parseTripViewSessionSnapshot(raw);
  } catch {
    return null;
  }
}

export function writeTripViewSessionSnapshot(
  snapshot: TripViewSessionSnapshot,
): void {
  if (suppressTripViewSnapshotWrites) {
    return;
  }
  try {
    sessionStorage.setItem(SNAPSHOT_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore quota / private mode
  }
}

export function isTripViewRestorePending(): boolean {
  try {
    return sessionStorage.getItem(RESTORE_PENDING_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function markTripViewRestorePending(): void {
  try {
    sessionStorage.setItem(RESTORE_PENDING_STORAGE_KEY, "true");
  } catch {
    // ignore
  }
}

export function readTripViewRestorePending(): boolean {
  return isTripViewRestorePending();
}

export function clearTripViewRestorePending(): void {
  try {
    sessionStorage.removeItem(RESTORE_PENDING_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Marks this history entry as session-restore hydrated (survives Strict Mode remount). */
export function markTripViewRestoreHydrated(locationKey: string): void {
  try {
    sessionStorage.setItem(RESTORE_HYDRATED_LOCATION_KEY, locationKey);
  } catch {
    // ignore
  }
}

export function isTripViewRestoreHydratedForLocation(
  locationKey: string,
): boolean {
  try {
    return (
      sessionStorage.getItem(RESTORE_HYDRATED_LOCATION_KEY) === locationKey
    );
  } catch {
    return false;
  }
}

export function clearTripViewRestoreHydrated(): void {
  try {
    sessionStorage.removeItem(RESTORE_HYDRATED_LOCATION_KEY);
  } catch {
    // ignore
  }
}

/**
 * Keeps browse landing mount after restorePending is cleared for this visit.
 */
export function resolveTripViewListPageMountFromPageMount(
  pageMount: TripViewPageMountResolution,
  locationKey: string,
): TripViewPageMountResolution {
  if (!isTripViewRestoreHydratedForLocation(locationKey)) {
    return pageMount;
  }
  if (pageMount.mode === "landing") {
    return pageMount;
  }
  return { mode: "landing", snapshot: null };
}

export function clearTripViewSession(): void {
  try {
    sessionStorage.removeItem(SNAPSHOT_STORAGE_KEY);
    sessionStorage.removeItem(RESTORE_PENDING_STORAGE_KEY);
    sessionStorage.removeItem(RESTORE_HYDRATED_LOCATION_KEY);
  } catch {
    // ignore
  }
}

export type TripViewPageMountNavigationType = "POP" | "PUSH" | "REPLACE";

export type ResolveTripViewPageMountOptions = {
  tripViewInAppSelect?: boolean;
  navigationType?: TripViewPageMountNavigationType;
  currentPathname?: string;
  /** True when the URL encodes trip-list filters (browse deep link / history). */
  hasListQueryInUrl?: boolean;
};

export type TripViewPageMountResolution = {
  mode: "landing" | "restore" | "default";
  snapshot: TripViewSessionSnapshot | null;
};

function consumeReloadTripViewEntryMount(
  currentPathname: string,
  tripIdParam: string | undefined,
  hasListQueryInUrl?: boolean,
): "landing" | "default" | null {
  if (!reloadDocumentSessionResetDone || reloadTripViewEntryMountConsumed) {
    return null;
  }
  if (
    reloadDocumentEntryPathname == null ||
    !isTripViewPathname(reloadDocumentEntryPathname)
  ) {
    return null;
  }
  if (currentPathname !== reloadDocumentEntryPathname) {
    return null;
  }
  reloadTripViewEntryMountConsumed = true;
  if (tripIdParam == null) {
    return hasListQueryInUrl ? "default" : "landing";
  }
  return "default";
}

export function resolveTripViewPageMount(
  tripIdParam: string | undefined,
  options?: ResolveTripViewPageMountOptions,
): TripViewPageMountResolution {
  if (consumeTripViewFreshEntryFromSectionRoot() && tripIdParam == null) {
    clearTripViewSession();
    return { mode: "landing", snapshot: null };
  }

  if (options?.currentPathname != null) {
    const reloadEntry = consumeReloadTripViewEntryMount(
      options.currentPathname,
      tripIdParam,
      options.hasListQueryInUrl,
    );
    if (reloadEntry === "landing") {
      return { mode: "landing", snapshot: null };
    }
    if (reloadEntry === "default") {
      return { mode: "default", snapshot: null };
    }
  }

  const isInAppTableSelectMount =
    options?.tripViewInAppSelect === true && options?.navigationType !== "POP";

  if (isInAppTableSelectMount) {
    clearTripViewRestorePending();
    clearTripViewRestoreHydrated();
    return { mode: "default", snapshot: null };
  }

  if (readTripViewRestorePending()) {
    clearTripViewRestorePending();
  }

  if (tripIdParam == null) {
    if (options?.hasListQueryInUrl === true) {
      return { mode: "default", snapshot: null };
    }
    return { mode: "landing", snapshot: null };
  }

  return { mode: "default", snapshot: null };
}

/** Browse landing UX (today, collapse reset) — entry URL only, not live query clears. */
export function isFreshBareBrowseTripListOnFirstMount(input: {
  entryHasListQueryInUrl: boolean;
  pageMountMode: TripViewPageMountResolution["mode"];
  initialRouteHadTripId: boolean;
}): boolean {
  if (input.entryHasListQueryInUrl) {
    return false;
  }
  return (
    input.pageMountMode === "landing" ||
    (input.pageMountMode === "default" && !input.initialRouteHadTripId)
  );
}

export type GetTripViewTabTargetOptions = {
  /** v1 browse landing: include viewer-local today in the URL (Towers Trip tab/sidebar). */
  browseLandingToday?: CalendarDay;
};

export function getTripViewTabTargetPath(
  legacyLastViewedTripId: string | null,
  useV1BrowseBehavior: boolean,
  options?: GetTripViewTabTargetOptions,
): string {
  const { pathname, search } = getTripViewTabTargetLocation(
    legacyLastViewedTripId,
    useV1BrowseBehavior,
    options,
  );
  if (search == null || search === "") {
    return pathname;
  }
  return `${pathname}?${search}`;
}

export function getTripViewTabTargetLocation(
  legacyLastViewedTripId: string | null,
  useV1BrowseBehavior: boolean,
  options?: GetTripViewTabTargetOptions,
): { pathname: string; search?: string } {
  if (!useV1BrowseBehavior) {
    return { pathname: tripViewPathForTripId(legacyLastViewedTripId) };
  }

  if (options?.browseLandingToday != null) {
    return {
      pathname: TRIP_VIEW_BROWSE_PATH,
      search: serializeTripViewBrowseLandingDaySearch(
        options.browseLandingToday,
      ),
    };
  }

  return { pathname: TRIP_VIEW_BROWSE_PATH };
}

function parseTripViewSessionSnapshot(
  raw: string,
): TripViewSessionSnapshot | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (parsed == null || typeof parsed !== "object") {
    return null;
  }

  const record = parsed as Record<string, unknown>;
  const pathname = record.pathname;
  if (typeof pathname !== "string" || !isTripViewPathname(pathname)) {
    return null;
  }

  return { pathname };
}
